import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import type { InfoGridProps } from "@/v2-backoffice-client/components/infoGrid"
import InfoGrid, {
  createInfoGridFields,
} from "@/v2-backoffice-client/components/infoGrid"
import type { AddOnCatalogFragment } from "@/v2-backoffice-client/license/addOns/catalog/__types__/license.addOn.catalog.fragment"

const FIELDS = createInfoGridFields<AddOnCatalogFragment>({
  fields: {
    // Scope is the add-on's defining fact: shared catalog add-ons are
    // grantable to anyone, bespoke ones only to their customer.
    Scope: {
      label: "Scope",
      accessor: (data) => (data.CustomerId ? "Bespoke" : "Catalog"),
      renderValue({ data }) {
        if (!data.CustomerId) return <Badge size="sm">Catalog</Badge>
        return (
          <span className="flex items-center gap-1.5">
            <Badge size="sm" variant="warning">
              Bespoke
            </Badge>
            {data.CustomerName && (
              <span className="truncate">{data.CustomerName}</span>
            )}
          </span>
        )
      },
    },
    // The license a customer must hold for the add-on to work.
    Products: {
      label: "Goes with",
      accessor: (data) =>
        data.Products.map((product) => product.Name).join(", ") ||
        "Any license",
      renderValue({ data }) {
        if (data.Products.length === 0) {
          return <span className="text-muted-foreground">Any license</span>
        }
        return (
          <span className="flex flex-wrap gap-1">
            {data.Products.map((product) => (
              <Badge key={product.Code} size="sm">
                {product.Name} license
              </Badge>
            ))}
          </span>
        )
      },
    },
    Code: {
      label: "Code",
      accessor: (data) => data.Code ?? null,
      renderValue({ data }) {
        if (!data.Code) return null
        return (
          <Badge size="sm" variant="neutral">
            {data.Code}
          </Badge>
        )
      },
    },
    Description: {
      label: "Description",
      accessor: (data) => data.Description ?? null,
      valueClassName: "truncate",
    },
    UpdatedAt: {
      label: "Updated",
      accessor: (data) =>
        data.UpdatedAt ? formatDateString(String(data.UpdatedAt)) : null,
      defaultHidden: true,
    },
    CreatedAt: {
      label: "Created",
      accessor: (data) =>
        data.CreatedAt ? formatDateString(String(data.CreatedAt)) : null,
      defaultHidden: true,
    },
    Id: {
      label: "Id",
      accessor: "Id",
      defaultHidden: true,
    },
  },
})

export type LicenseAddOnViewInfoProps = Omit<
  InfoGridProps<AddOnCatalogFragment>,
  "data" | "fields"
> & {
  addOn: AddOnCatalogFragment | null | undefined
}

function LicenseAddOnViewInfo(props: LicenseAddOnViewInfoProps) {
  const { addOn, className, ...infoGridProps } = props

  return (
    <InfoGrid
      id="license-add-on-info"
      className={cn("license-add-on-view-info", className)}
      data={addOn}
      fields={FIELDS}
      {...infoGridProps}
    />
  )
}

export default LicenseAddOnViewInfo
